/**
 * Reads HX-Trigger headers from a fetch Response and dispatches
 * them as native DOM CustomEvents on document.body.
 *
 * This bridges custom fetch() calls with HTMX's event-driven UI updates.
 * Works with: hx-trigger="event-name from:body"
 */

export type ToastType = "info" | "success" | "warning" | "error";

/**
 * Dispatches a toast notification via the existing flash-message event.
 */
export function dispatchToast(msg: string, type: ToastType = "info") {
  document.body.dispatchEvent(
    new CustomEvent("flash-message", {
      bubbles: true,
      detail: { msg, type },
    }),
  );
}

/**
 * Returns the swap target of an htmx:afterSettle event, or null when that
 * target is no longer in the document.
 *
 * Use this before calling Alpine.initTree on a settled target. With an
 * outerHTML swap, detail.target is the old element that the swap replaced.
 * By afterSettle it is detached, and Alpine's MutationObserver has already
 * run destroyTree on it (which clears the _x_marker initTree checks) and
 * initialized the new content. Calling initTree on the detached tree runs
 * every directive again in a subtree with no parent, so expressions that
 * read an ancestor's x-data throw "X is not defined". That produced the
 * 12 "activeTab is not defined" errors on the project edit page
 * (GlitchTip STUDIO-BROWSER-16).
 */
export function liveSettleTarget(e: Event): HTMLElement | null {
  const target = ((e as CustomEvent).detail?.target ?? e.target) as HTMLElement | null;
  if (!target || target.isConnected === false) return null;
  return target;
}

/**
 * Safely retrieves the CSRF token from the meta tag.
 */
export function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta ? meta.getAttribute("content") || "" : "";
}

/**
 * Centralized setup for CSRF protection across HTMX and build pipeline.
 * Registers the HTMX listener on 'document' to avoid racing conditions with 'body'.
 */
export function setupCSRF() {
  // Attach the current CSRF token to every outgoing HTMX request.
  document.addEventListener("htmx:configRequest", (evt: any) => {
    const token = getCSRFToken();
    if (token) {
      evt.detail.headers["X-CSRF-Token"] = token;
    }
  });

  // Keep the meta tag fresh: the server sends the current token in the
  // X-CSRF-Token response header. With hx-boost the <head> is never
  // re-rendered, so without this the token goes stale after cookie rotation.
  document.addEventListener("htmx:afterOnLoad", (evt: any) => {
    const token = evt.detail.xhr?.getResponseHeader("X-CSRF-Token");
    if (token) {
      const meta = document.querySelector('meta[name="csrf-token"]');
      if (meta) meta.setAttribute("content", token);
    }
  });
}

const GENERIC_ERROR_MESSAGE = "An unexpected error occurred";

// Headers htmx turns into DOM events (see processResponseTriggers below).
const TRIGGER_HEADERS = ["HX-Trigger", "HX-Trigger-After-Swap", "HX-Trigger-After-Settle"];

/**
 * Picks the toast text for a failed htmx response, or null when the response
 * already explains itself. A handler that calls TriggerErrorToast sends its own
 * `flash-message` via HX-Trigger (htmx fires it even on a 4xx), and one that
 * renders the error inline retargets the swap (HX-Retarget). Toasting on top of
 * either stacked a second, generic "An unexpected error occurred" toast over the
 * specific one. Everything else keeps a toast: the JSON `message`, a plain-text
 * body, or the generic text for a bodiless 5xx.
 */
export function responseErrorMessage(xhr: any): string | null {
  const header = (name: string): string => xhr?.getResponseHeader?.(name) || "";
  if (TRIGGER_HEADERS.some((h) => header(h).includes("flash-message"))) return null;
  if (header("HX-Retarget")) return null;

  let message = GENERIC_ERROR_MESSAGE;
  try {
    // Check for JSON error response via Content-Type header
    if (header("Content-Type").includes("application/json")) {
      const data = typeof xhr.response === "object" ? xhr.response : JSON.parse(xhr.responseText);
      message = data.message || data.error || message;
    } else {
      // Fallback for plain text errors (like CSRF_DEBUG_ERROR)
      message = xhr.responseText || message;
    }
  } catch (e) {
    console.warn("[htmx] Failed to parse error response", e);
    if (xhr.status === 403) message = "Permission denied";
    if (xhr.status === 404) message = "Resource not found";
  }
  return message;
}

/**
 * Registers global HTMX event listeners for error handling.
 */
export function registerGlobalHTMXListeners() {
  document.body.addEventListener("htmx:responseError", (event: any) => {
    const message = responseErrorMessage(event.detail.xhr);
    if (message !== null) dispatchToast(message, "error");
  });
}

/**
 * Toasts the server's explanation for a failed raw-`fetch()` response.
 *
 * The htmx:responseError listener above only fires for htmx-issued requests, so
 * anything sent via fetch() (the wizard autosave engines, CMS saves) drops the
 * server's message on the floor unless it calls this. That silence is how a
 * plan-limit rejection used to show up as nothing but a 4px red ✗ next to a
 * field, with the actual reason visible only in the server log.
 *
 * Returns true if a toast was shown, so callers can distinguish "the user has
 * been told why" from "this failed for reasons we can't explain".
 */
export async function toastFetchError(resp: Response): Promise<boolean> {
  try {
    if ((resp.headers.get("Content-Type") || "").includes("application/json")) {
      const data = await resp.clone().json();
      const message = data?.message || data?.error;
      if (message) {
        dispatchToast(message, "error");
        return true;
      }
    }
  } catch {
    /* body wasn't JSON or was already consumed — fall through silently */
  }
  return false;
}
export function processResponseTriggers(resp: Response): void {
  ["HX-Trigger", "HX-Trigger-After-Swap", "HX-Trigger-After-Settle"].forEach((header) => {
    const val = resp.headers.get(header);
    if (!val) return;

    let triggers: Record<string, any>;
    try {
      // Triggers can be simple strings or JSON objects
      triggers = JSON.parse(val);
    } catch {
      triggers = { [val]: true };
    }

    Object.entries(triggers).forEach(([name, detail]) => {
      document.body.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
    });
  });
}

/**
 * Performs a fetch request with JSON body and processes HTMX OOB swaps from the response.
 * Use this when you need to send complex data (like arrays) that htmx.ajax handles poorly,
 * but still want the benefits of backend-driven UI updates.
 *
 * @param method - HTTP method (e.g., 'POST', 'DELETE')
 * @param url - The endpoint URL
 * @param body - The object to be sent as JSON
 */
export async function fetchWithOOB(method: string, url: string, body: any): Promise<Response> {
  const resp = await fetch(url, {
    method,
    credentials: "same-origin",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "HX-Request": "true", // Helpful for server-side detection
      "X-CSRF-Token": getCSRFToken(),
    },
    body: JSON.stringify(body),
  });

  if (resp.ok) {
    processResponseTriggers(resp);
    const html = await resp.text();
    if (html.trim()) {
      processOOBSwaps(html);
    }
  } else {
    // Centralized error handling for fetchWithOOB
    let message = `Request failed (${resp.status})`;
    try {
      const data = await resp.json();
      message = data.message || data.error || message;
    } catch {
      // Fallback for non-JSON or unparseable errors
    }
    dispatchToast(message, "error");
  }

  return resp;
}

/**
 * Manually processes HTMX OOB swaps from an HTML string.
 * This is necessary because htmx.process() only initializes HTMX attributes,
 * it doesn't trigger OOB swaps which are normally only processed when HTMX
 * receives a response directly.
 */
export function processOOBSwaps(html: string): void {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const oobElements = doc.querySelectorAll("[hx-swap-oob]");

  // Pre-flight: if every non-delete OOB target is missing from the current DOM,
  // the response is for a page the user has since navigated away from. Skipping
  // the whole batch avoids partial-application bugs (e.g. half the swaps land
  // because some IDs are global, the other half don't because they're page-scoped).
  let required = 0;
  let missing = 0;
  oobElements.forEach((el) => {
    const swapType = el.getAttribute("hx-swap-oob") || "true";
    if (swapType === "delete" || !el.id) return;
    required++;
    if (!document.getElementById(el.id)) missing++;
  });
  if (required > 0 && missing === required) {
    console.warn(
      `[fetchWithOOB] skipping OOB batch — all ${required} targets are missing (stale response from a previous page)`,
    );
    return;
  }

  oobElements.forEach((el) => {
    const swapType = el.getAttribute("hx-swap-oob") || "true";
    const targetId = el.id;

    if (!targetId) {
      console.warn("[fetchWithOOB] OOB element missing id:", el);
      return;
    }

    const target = document.getElementById(targetId);
    if (!target) {
      // Silently ignore if the intended action was to delete it anyway
      if (swapType === "delete") return;
      console.warn(`[fetchWithOOB] OOB target not found: #${targetId}`);
      return;
    }

    // Handle different swap types
    if (swapType === "delete") {
      target.remove();
    } else if (swapType === "true" || swapType === "innerHTML") {
      target.innerHTML = el.innerHTML;
    } else if (swapType === "outerHTML") {
      target.outerHTML = el.outerHTML;
    } else if (swapType === "beforebegin") {
      target.insertAdjacentHTML("beforebegin", el.outerHTML);
    } else if (swapType === "afterbegin") {
      target.insertAdjacentHTML("afterbegin", el.innerHTML);
    } else if (swapType === "beforeend") {
      target.insertAdjacentHTML("beforeend", el.innerHTML);
    } else if (swapType === "afterend") {
      target.insertAdjacentHTML("afterend", el.outerHTML);
    } else {
      // Default to innerHTML for unknown swap types
      target.innerHTML = el.innerHTML;
    }

    // Re-initialize HTMX/Alpine on the swapped content so any hx-* attributes / Alpine components work
    // @ts-ignore - htmx is global from CDN
    if (window.htmx && swapType !== "delete") {
      // @ts-ignore
      window.htmx.process(target);
    }
    // Only init Alpine if the element itself declares x-data (standalone component like toast-trigger)
    // This prevents re-initializing elements that are already part of a parent Alpine scope
    // @ts-ignore
    const hasOwnXData = target.hasAttribute("x-data") || target.querySelector("[x-data]");
    if (window.Alpine && swapType !== "delete" && hasOwnXData) {
      // @ts-ignore
      window.Alpine.initTree(target);
    }
  });
}
